import { useEffect, useRef } from 'react';

import type { NoteTelemetryEventType,SelectionMetrics } from '../utils/analytics/noteTelemetry';

export type TextareaAnalyticsHookOptions = {
  docId: string;
  getDocVersion: () => string;
  getPlainText: () => string;
  settleMs?: number;
  throttleMs?: number;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
  };
  onEvent: (type: NoteTelemetryEventType, payload: SelectionMetrics) => void;
};

/**
 * Hook to track textarea selection and copy analytics for plain text editing
 * Works with native HTML textarea elements
 */
export function useTextareaSelectionAnalytics(
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  opts: TextareaAnalyticsHookOptions,
) {
  const settleMs = opts.settleMs ?? 1000; // 1 second default
  const throttleMs = opts.throttleMs ?? 100;

  // Running state for a selection gesture
  const state = useRef<{
    selectionId?: string;
    startedAt?: number;
    lastChangedAt?: number;
    lastLen: number;
    settleTimer?: number;
    lastInput?: 'mouse' | 'touch' | 'keyboard';
  }>({ lastLen: 0 });

  const now = () => performance.now();
  const uuid = () => crypto.randomUUID();

  // Modality tracker (consistent across contexts): pointerdown + keydown
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const controller = new AbortController();
    const { signal } = controller;

    const onPointerDown = (e: PointerEvent) => {
      state.current.lastInput = e.pointerType === 'touch' ? 'touch' : 'mouse';
    };
    const onKeyDown = () => {
      state.current.lastInput = 'keyboard';
    };

    textarea.addEventListener('pointerdown', onPointerDown, { signal });
    textarea.addEventListener('keydown', onKeyDown, { signal });

    return () => {
      controller.abort();
    };
  }, [textareaRef]);

  // Throttle helper
  const lastFire = useRef(0);
  const shouldThrottle = () => {
    const t = now();
    if (t - lastFire.current < throttleMs) return true;
    lastFire.current = t;
    return false;
  };

  // Selection change handler (listen to select, keyup, mouseup events)
  const handleSelectionChange = () => {
    if (!textareaRef.current || shouldThrottle()) return;

    const textarea = textareaRef.current;
    const startOffset = textarea.selectionStart;
    const endOffset = textarea.selectionEnd;
    const len = endOffset - startOffset;
    const t = now();

    // Start of a new selection gesture
    if (len > 0 && !state.current.startedAt) {
      state.current.selectionId = uuid();
      state.current.startedAt = t;
    }

    // Update timing + (re)arm settle timer
    if (len > 0) {
      state.current.lastChangedAt = t;
      if (state.current.settleTimer) {
        clearTimeout(state.current.settleTimer);
      }

      state.current.settleTimer = window.setTimeout(async () => {
        if (!state.current.startedAt || !textareaRef.current) return;

        // Re-check selection when timer fires
        const currentTextarea = textareaRef.current;
        const currentStart = currentTextarea.selectionStart;
        const currentEnd = currentTextarea.selectionEnd;
        const currentLen = currentEnd - currentStart;

        if (currentLen === 0) return;

        const settleTime = now();
        const plainText = opts.getPlainText();
        const selectedText = plainText.substring(currentStart, currentEnd);

        const payload: SelectionMetrics = {
          selectionId: state.current.selectionId!,
          docId: opts.docId,
          docVersion: opts.getDocVersion(),
          chars: {
            selected: currentLen,
            total: plainText.length,
            percent: plainText.length ? currentLen / plainText.length : 0,
          },
          timings: {
            startedAt: state.current.startedAt,
            lastChangedAt: state.current.lastChangedAt!,
            settledAt: settleTime,
            msToSettle: settleTime - state.current.startedAt,
          },
          modality: state.current.lastInput,
          isBackward: false, // Textarea selections are always forward
          ua: navigator.userAgent,
          metadata: opts.metadata,
        };

        opts.onEvent('text_selected', payload);
      }, settleMs);
    }

    // Selection cleared (end of gesture) — reset timers
    if (len === 0 && state.current.startedAt) {
      state.current.selectionId = undefined;
      state.current.startedAt = undefined;
      state.current.lastChangedAt = undefined;
      if (state.current.settleTimer) {
        clearTimeout(state.current.settleTimer);
      }
      state.current.settleTimer = undefined;
    }

    state.current.lastLen = len;
  };

  // Listen to selection events
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const controller = new AbortController();
    const { signal } = controller;

    textarea.addEventListener('select', handleSelectionChange, { signal });
    textarea.addEventListener('keyup', handleSelectionChange, { signal });
    textarea.addEventListener('mouseup', handleSelectionChange, { signal });

    return () => {
      controller.abort();
      if (state.current.settleTimer) {
        clearTimeout(state.current.settleTimer);
      }
    };
  }, [textareaRef, opts, settleMs, throttleMs]);

  // Copy / cut handlers
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const controller = new AbortController();
    const { signal } = controller;

    const handler = (type: 'text_copied') => async () => {
      const startOffset = textarea.selectionStart;
      const endOffset = textarea.selectionEnd;
      const len = endOffset - startOffset;
      if (len === 0) return;

      const plainText = opts.getPlainText();
      const selectedText = plainText.substring(startOffset, endOffset);
      const t = now();

      const payload: SelectionMetrics = {
        selectionId: state.current.selectionId ?? uuid(),
        docId: opts.docId,
        docVersion: opts.getDocVersion(),
        chars: {
          selected: len,
          total: plainText.length,
          percent: plainText.length ? len / plainText.length : 0,
        },
        timings: {
          startedAt: state.current.startedAt ?? t,
          lastChangedAt: state.current.lastChangedAt ?? t,
          copiedAt: t,
          msToCopy: state.current.startedAt ? t - state.current.startedAt : 0,
        },
        modality: state.current.lastInput,
        isBackward: false,
        ua: navigator.userAgent,
        metadata: opts.metadata,
      };

      opts.onEvent(type, payload);
    };

    const onCopy = () => handler('text_copied')();
    const onCut = () => handler('text_copied')();

    textarea.addEventListener('copy', onCopy, { signal });
    textarea.addEventListener('cut', onCut, { signal });

    return () => {
      controller.abort();
    };
  }, [textareaRef, opts]);
}
